import { LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { AppRoutesPaths } from '@/common/constants/routes';
import { useAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { AppPill } from '@/ui/pill';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CourseCustomizationLanguageSelector } from '../CourseCustomizationLanguageSelector';

const OPTION_CARD_WIDTH = '520px';

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 52px;
  padding-top: 36px;
  width: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const Title = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: var(--heading-medium-font-size);
  line-height: var(--default-line-height);
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const Description = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: var(--medium-font-size);
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const LanguageSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const AdvancedSettingsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: ${OPTION_CARD_WIDTH};
  max-width: 100%;
  font-family: var(--font-family);
  font-weight: 400;
  font-size: var(--xsmall-font-size);
  line-height: 1.4;
  color: var(--black-main);
  text-align: center;

  .chevron-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s;

    &.expanded {
      transform: rotate(180deg);
    }
  }
`;

const AdvancedSettingsContent = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 19px;
  width: ${OPTION_CARD_WIDTH};
  max-width: 100%;
  margin-top: 10px;
  align-items: center;
  background: var(--white-main);
  border-radius: 9px;
  padding: 22px;
`;

const CustomizationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 19px;
  width: 100%;
  align-items: flex-start;
`;

const SectionLabel = styled.label`
  display: block;
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  color: var(--black-main);
  margin-bottom: 9px;
  text-align: left;
  height: 13px;
`;

const CheckboxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-items: flex-start;
  width: 100%;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
  width: 100%;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: auto;

  &.content-type {
    min-width: auto;
    gap: 9px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  color: var(--black-main);
  cursor: pointer;
  white-space: nowrap;

  .info-icon {
    color: var(--gray-darker);
    font-size: 16px;
    cursor: help;
    width: 16px;
    height: 16px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.p-checkbox {
    width: 18px;
    height: 18px;

    .p-checkbox-box {
      width: 18px;
      height: 18px;
      border-radius: 2px;
      border: 1px solid var(--gray-darker);
      background: var(--white-main);

      .p-checkbox-icon {
        font-size: 10px;
        color: var(--white-main);
      }
    }

    &.p-highlight .p-checkbox-box {
      background: var(--red-main);
      border-color: var(--red-main);
    }
  }
`;

const PointsOfContactGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
`;

const PointsOfContactLink = styled.a`
  font-family: var(--font-family);
  font-weight: var(--medium-font-weight);
  font-size: var(--xsmall-font-size);
  line-height: 1.4;
  color: var(--red-main);
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: var(--red-dark);
  }
`;

export type CustomizeCoursePreferencesStepProps = {
  course: Course;
  selectedLanguage?: LanguagesEnum;
  onLanguageChange: (language: LanguagesEnum | undefined) => void;
  isDisabled?: boolean;
};

export const CustomizeCoursePreferencesStep: React.FC<
  CustomizeCoursePreferencesStepProps
> = ({ course, selectedLanguage, onLanguageChange, isDisabled = false }) => {
  const { t } = useTranslation();
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [organizationContext] = useState(true);
  const [customizeQuizzes] = useState(true);
  const [customizeSlides] = useState(true);

  const currentAccount = useAppSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const showPointsOfContact = canUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
  );
  const { itContactConfigured } = useAccountVariables(currentAccount?.id, {
    enabled: showPointsOfContact,
  });

  return (
    <ContentSection>
      {/* Title and Description */}
      <TitleSection>
        <Title>{t('course.customization.details.title')}</Title>
        <Description>{t('course.customization.description')}</Description>
      </TitleSection>

      {/* Language Selector */}
      <LanguageSelectorContainer>
        <CourseCustomizationLanguageSelector
          course={course}
          selectedLanguage={selectedLanguage}
          onChange={onLanguageChange}
          isDisabled={isDisabled}
        />

        <AdvancedSettingsToggle
          type="button"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          <span>{t('course.customization.showAdvancedSettings')}</span>
          <div
            className={`chevron-icon ${showAdvancedSettings ? 'expanded' : ''}`}
          >
            <i className="pi pi-chevron-down" />
          </div>
        </AdvancedSettingsToggle>

        {/* Advanced Settings Content */}
        <AdvancedSettingsContent visible={showAdvancedSettings}>
          <CustomizationSection>
            <div style={{ width: '100%' }}>
              <SectionLabel>
                {t('course.customization.aiSettings')}
              </SectionLabel>
              <CheckboxesContainer>
                <CheckboxRow>
                  <CheckboxGroup>
                    <StyledCheckbox
                      inputId="org-context"
                      checked={organizationContext}
                      onChange={() => undefined}
                      disabled
                      readOnly
                    />
                    <CheckboxLabel htmlFor="org-context">
                      {t('course.customization.organizationContext')}
                      <Tooltip
                        target=".info-icon-org-context"
                        content={t(
                          'course.customization.organizationContext.tooltip',
                        )}
                      />
                      <i className="pi pi-info-circle info-icon info-icon-org-context" />
                    </CheckboxLabel>
                  </CheckboxGroup>

                  {showPointsOfContact && (
                    <PointsOfContactGroup>
                      <StyledCheckbox
                        inputId="points-of-contact"
                        checked={itContactConfigured}
                        onChange={() => undefined}
                        disabled
                        readOnly
                      />
                      <CheckboxLabel htmlFor="points-of-contact">
                        {t('course.customization.pointsOfContact')}
                        <Tooltip
                          className="points-of-contact-tooltip"
                          target=".info-icon-points-of-contact"
                          content={t(
                            'course.customization.pointsOfContact.tooltip',
                          )}
                        />
                        <i className="pi pi-info-circle info-icon info-icon-points-of-contact" />
                      </CheckboxLabel>
                      <AppPill tone="success">
                        {t('course.customization.pointsOfContact.recommended')}
                      </AppPill>
                      {!itContactConfigured && !currentAccount?.isSystem && (
                        <PointsOfContactLink
                          href={AppRoutesPaths.ACCOUNT_TRAINING_SETTINGS_ROUTE}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('course.customization.pointsOfContact.setupLink')}
                        </PointsOfContactLink>
                      )}
                    </PointsOfContactGroup>
                  )}
                </CheckboxRow>
              </CheckboxesContainer>
            </div>

            <div style={{ width: '100%' }}>
              <SectionLabel>
                {t('course.customization.contentType')}
              </SectionLabel>
              <CheckboxRow>
                <CheckboxGroup className="content-type">
                  <StyledCheckbox
                    inputId="customize-quizzes"
                    checked={customizeQuizzes}
                    onChange={() => undefined}
                    disabled
                    readOnly
                  />
                  <CheckboxLabel htmlFor="customize-quizzes">
                    {t('course.customization.quiz')}
                  </CheckboxLabel>
                </CheckboxGroup>

                <CheckboxGroup className="content-type">
                  <StyledCheckbox
                    inputId="customize-slides"
                    checked={customizeSlides}
                    onChange={() => undefined}
                    disabled
                    readOnly
                  />
                  <CheckboxLabel htmlFor="customize-slides">
                    {t('course.customization.presentation')}
                  </CheckboxLabel>
                </CheckboxGroup>
              </CheckboxRow>
            </div>
          </CustomizationSection>
        </AdvancedSettingsContent>
      </LanguageSelectorContainer>
    </ContentSection>
  );
};
